import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT, COLORS, BOSS_DEFINITIONS } from '../config';
import { GoldDisplay } from '../ui/GoldDisplay';
import { WaveDisplay } from '../ui/WaveDisplay';
import { WallHPBar } from '../ui/WallHPBar';
import { BuffBar, ActiveBuff } from '../ui/BuffBar';
import { InGameMenu } from '../ui/InGameMenu';
import { DOMUI } from '../ui/DOMUI';
import { PrestigeTreeUI } from '../ui/PrestigeTreeUI';
import { LegacyBar } from '../ui/LegacyBar';
import { SkillBar } from '../ui/SkillBar';
import { Enemy } from '../objects/Enemy';
import { GameScene } from './GameScene';
import { formatNumber } from '../utils/formatNumber';

export class UIScene extends Phaser.Scene {
  private goldDisplay!: GoldDisplay;
  private waveDisplay!: WaveDisplay;
  private wallHPBar!: WallHPBar;
  private domUI!: DOMUI;
  private prestigeTreeUI!: PrestigeTreeUI;
  private buffBar!: BuffBar;
  private inGameMenu!: InGameMenu;
  private gameScene!: GameScene;
  private topBarBg!: Phaser.GameObjects.Graphics;
  private isPaused: boolean = false;
  private skillBar!: SkillBar;
  private legacyBar!: LegacyBar;
  private pauseOverlay: Phaser.GameObjects.Graphics | null = null;
  private pauseText: Phaser.GameObjects.Text | null = null;

  // Speed controls
  private gameSpeed: number = 1;
  private speedButtons: { gfx: Phaser.GameObjects.Graphics; text: Phaser.GameObjects.Text; speed: number }[] = [];
  private repairDisplay!: Phaser.GameObjects.Text;

  // Layout constants for top-right
  private readonly BTN_SIZE = 28;
  private readonly SPEED_BTN_W = 28;
  private readonly SPEED_BTN_H = 20;
  private readonly SPEED_GAP = 3;

  private speedStartX = 0;
  private speedY = 0;

  constructor() {
    super({ key: 'UIScene' });
  }

  create(): void {
    this.gameScene = this.scene.get('GameScene') as GameScene;
    this.isPaused = false;
    this.gameSpeed = 1;

    // Top bar background
    this.topBarBg = this.add.graphics();
    this.topBarBg.fillStyle(COLORS.uiBackground, 0.7);
    this.topBarBg.fillRect(0, 0, GAME_WIDTH, 36);

    // HUD elements
    this.waveDisplay = new WaveDisplay(this, 12, 8);
    this.wallHPBar = new WallHPBar(this, GAME_WIDTH / 2 - 80, 12, 160, 14);

    // Calculate layout positions from right edge:
    // [achieve] 8px [5x] 3px [3x] 3px [1x] 12px [gold+income]
    const rightPad = 8;
    const topY = 4;

    const achieveX = GAME_WIDTH - rightPad - this.BTN_SIZE;

    // Adjust speed buttons to account for new achievement button
    const speedRightEdge = achieveX - 8;
    this.speedStartX = speedRightEdge - 3 * this.SPEED_BTN_W - 2 * this.SPEED_GAP;
    this.speedY = topY + (this.BTN_SIZE - this.SPEED_BTN_H) / 2; // vertically center with buttons

    // Gold display: right-aligned, left of speed buttons
    const goldAnchorRight = this.speedStartX - 8;
    this.goldDisplay = new GoldDisplay(this, goldAnchorRight, 8);

    // Buff indicator bar: below top bar, right-aligned to match gold area
    this.buffBar = new BuffBar(this, goldAnchorRight, 38);

    // Repair rate display to the right of Wall HP
    const wallRightEdge = (GAME_WIDTH / 2) + 80;
    this.repairDisplay = this.add.text(wallRightEdge + 10, 12, '', {
      fontSize: '10px',
      fontFamily: 'Arial, sans-serif',
      color: '#44aa44', // Green for repair
      fontStyle: 'bold'
    }).setOrigin(0, 0);

    // Initialize DOM UI Controller
    this.domUI = new DOMUI((key: string) => {
      // Route purchases/upgrades back to the GameScene logic
      if (this.gameScene) {
        if (key.startsWith('buy_')) {
          const qty = this.domUI.buyMultiplier;
          this.gameScene.buyUnit(key, qty);
        } else if (key.startsWith('upgrade_')) {
          this.gameScene.buyUnit(key); // Same method handles upgrades
        } else if (key === 'reset_game') {
          this.gameScene.buyUnit(key);
        } else if (key === 'prestige') {
          this.gameScene.resetForPrestige();
          this.prestigeTreeUI.show();
        } else if (key === 'open_prestige_tree') {
          this.prestigeTreeUI.show();
        } else if (key.startsWith('elder_')) {
          // old layout, mostly unused now
          const parts = key.split('_');
          const elderName = parts[1] as import('../types').ElderName;
          this.gameScene.prestigeManager.purchaseElderTier(elderName);
        }
      }
    });

    // Initialize Prestige Tree UI
    this.prestigeTreeUI = new PrestigeTreeUI(this.gameScene.prestigeManager);
    this.prestigeTreeUI.attachScene(this.gameScene);

    // In-game menu overlay
    this.inGameMenu = new InGameMenu(
      this,
      () => { this.resumeFromMenu(); },
      () => { this.goToMainMenu(); }
    );

    // Skill Bar
    this.skillBar = new SkillBar(this, this.gameScene);

    // Create top right buttons
    this.createSpeedButtons();

    // Legacy progress bar — full-width bar just below the top HUD
    this.legacyBar = new LegacyBar(this, 36);
  }



  private createSpeedButtons(): void {
    const speeds = [1, 3, 5];
    let currentX = this.speedStartX;

    for (const speed of speeds) {
      const x = currentX;
      const gfx = this.add.graphics();
      const isActive = speed === this.gameSpeed;
      this.drawSpeedButton(gfx, x, this.speedY, this.SPEED_BTN_W, this.SPEED_BTN_H, isActive);

      const text = this.add.text(x + this.SPEED_BTN_W / 2, this.speedY + this.SPEED_BTN_H / 2, `${speed}x`, {
        fontSize: '10px',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold',
        color: isActive ? '#ffffff' : '#aaaaaa',
      }).setOrigin(0.5);

      const hit = this.add.rectangle(x + this.SPEED_BTN_W / 2, this.speedY + this.SPEED_BTN_H / 2, this.SPEED_BTN_W, this.SPEED_BTN_H)
        .setInteractive({ useHandCursor: true })
        .setAlpha(0.001);

      hit.on('pointerdown', () => {
        this.setGameSpeed(speed);
      });

      this.speedButtons.push({ gfx, text, speed });
      currentX += this.SPEED_BTN_W + this.SPEED_GAP;
    }
  }

  private drawSpeedButton(gfx: Phaser.GameObjects.Graphics, x: number, y: number, w: number, h: number, active: boolean): void {
    gfx.clear();
    if (active) {
      gfx.fillStyle(0x44aa44, 0.9);
    } else {
      gfx.fillStyle(COLORS.uiBackgroundLight, 0.7);
    }
    gfx.fillRoundedRect(x, y, w, h, 4);
    gfx.lineStyle(1, active ? 0x66cc66 : 0x666666, 0.5);
    gfx.strokeRoundedRect(x, y, w, h, 4);
  }

  private setGameSpeed(speed: number): void {
    this.gameSpeed = speed;

    let currentX = this.speedStartX;
    for (const btn of this.speedButtons) {
      const isActive = btn.speed === speed;
      this.drawSpeedButton(btn.gfx, currentX, this.speedY, this.SPEED_BTN_W, this.SPEED_BTN_H, isActive);
      btn.text.setColor(isActive ? '#ffffff' : '#aaaaaa');
      currentX += this.SPEED_BTN_W + this.SPEED_GAP;
    }

    this.gameScene.gameSpeedMultiplier = speed;
  }

  private drawIconButton(gfx: Phaser.GameObjects.Graphics, x: number, y: number, size: number, color: number): void {
    gfx.fillStyle(color, 0.9);
    gfx.fillRoundedRect(x, y, size, size, 5);
    gfx.lineStyle(1, 0xffffff, 0.15);
    gfx.strokeRoundedRect(x, y, size, size, 5);
  }


  private drawTrophyIcon(gfx: Phaser.GameObjects.Graphics, x: number, y: number, size: number): void {
    const cx = x + size / 2;
    const cy = y + size / 2;
    gfx.fillStyle(0xffd700, 1);

    // Cup part
    gfx.beginPath();
    gfx.moveTo(cx - 5, cy - 4);
    gfx.lineTo(cx + 5, cy - 4);
    gfx.lineTo(cx + 3, cy + 2);
    gfx.lineTo(cx - 3, cy + 2);
    gfx.closePath();
    gfx.fillPath();

    // Base
    gfx.fillRect(cx - 2, cy + 2, 4, 2); // stem
    gfx.fillRect(cx - 4, cy + 4, 8, 2); // foot

    // Handles (lines)
    gfx.lineStyle(1, 0xffd700);
    gfx.beginPath();
    gfx.moveTo(cx - 5, cy - 3);
    gfx.lineTo(cx - 7, cy - 3);
    gfx.lineTo(cx - 7, cy);
    gfx.lineTo(cx - 4, cy + 1);
    gfx.strokePath();

    gfx.beginPath();
    gfx.moveTo(cx + 5, cy - 3);
    gfx.lineTo(cx + 7, cy - 3);
    gfx.lineTo(cx + 7, cy);
    gfx.lineTo(cx + 4, cy + 1);
    gfx.strokePath();
  }

  private togglePause(): void {
    this.isPaused = !this.isPaused;

    if (this.isPaused) {
      this.scene.pause('GameScene');
      this.pauseOverlay = this.add.graphics();
      this.pauseOverlay.fillStyle(0x000000, 0.4);
      this.pauseOverlay.fillRect(0, 36, GAME_WIDTH, GAME_HEIGHT - 36);

      this.pauseText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 30, 'PAUSED', {
        fontSize: '48px',
        fontFamily: 'Arial Black, Arial, sans-serif',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 4,
      }).setOrigin(0.5);
    } else {
      this.scene.resume('GameScene');
      if (this.pauseOverlay) { this.pauseOverlay.destroy(); this.pauseOverlay = null; }
      if (this.pauseText) { this.pauseText.destroy(); this.pauseText = null; }
    }
  }

  private handleMenuClick(): void {
    if (this.inGameMenu.getIsVisible()) {
      // Menu already open — close it and resume
      this.inGameMenu.hide();
      this.resumeFromMenu();
      return;
    }

    // Pause game and show in-game menu
    if (!this.isPaused) {
      this.scene.pause('GameScene');
      this.isPaused = true;
    }
    this.inGameMenu.show();
  }

  private resumeFromMenu(): void {
    if (this.isPaused) {
      this.isPaused = false;
      this.scene.resume('GameScene');
    }
    // Clean up any existing pause overlay
    if (this.pauseOverlay) { this.pauseOverlay.destroy(); this.pauseOverlay = null; }
    if (this.pauseText) { this.pauseText.destroy(); this.pauseText = null; }
  }

  private goToMainMenu(): void {
    if (this.isPaused) {
      this.scene.resume('GameScene');
    }
    this.scene.stop('GameScene');
    this.scene.start('MenuScene');
  }

  update(time: number, delta: number): void {
    if (!this.gameScene) return;

    // Update gold display
    this.goldDisplay.update(this.gameScene.economyManager.gold);

    // Update gold rush glow on gold display
    this.goldDisplay.setGoldRush(this.gameScene.goldRushActive, this.gameScene.goldRushMultiplier);

    // Update buff bar
    const activeBuffs: ActiveBuff[] = [];
    if (this.gameScene.goldRushActive) {
      activeBuffs.push({
        id: 'gold_rush',
        color: 0xffcc00,
        remainingMs: this.gameScene.goldRushTimer,
        totalMs: this.gameScene.goldRushDuration,
        description: `Gold Rush: ${this.gameScene.goldRushMultiplier}x gold from kills`,
      });
    }
    this.buffBar.update(activeBuffs);

    // Always update wave display, wall HP, and shop buttons
    this.waveDisplay.update(
      this.gameScene.waveManager.currentWave,
      this.gameScene.waveManager.isWaveActive,
      this.gameScene.isShopPhase
    );
    this.wallHPBar.update(this.gameScene.wall.currentHP, this.gameScene.wall.maxHP);

    // Update DOM UI
    if (this.gameScene && this.gameScene.economyManager) {
      this.domUI.elderBonuses = this.gameScene.elderBonuses;
      this.domUI.updateButtons(this.gameScene.economyManager, this.gameScene.prestigeManager);
      this.domUI.updatePrestigeTab(
        this.gameScene.prestigeManager,
        this.gameScene.waveManager.currentWave,
        this.gameScene.goldEarnedThisRun
      );
    }

    // Update legacy bar
    if (this.legacyBar && this.gameScene) {
      this.legacyBar.update(
        this.gameScene.goldEarnedThisRun,
        this.gameScene.prestigeManager.getTotalLegacy(),
        delta
      );
    }

    if (this.skillBar && this.gameScene.skillManager) {
      this.skillBar.update(this.gameScene.skillManager);
    }

    // Update repair rate display
    const repairRate = this.gameScene.getTotalRepairRate();
    if (repairRate > 0) {
      this.repairDisplay.setText(`+${formatNumber(repairRate)} HP/s`);
    } else {
      this.repairDisplay.setText('');
    }

    // Boss HP Bar Logic
    if (this.gameScene.activeBoss && !this.gameScene.activeBoss.isDead) {
      if (this.bossTooltipTimer > 0) {
        this.bossTooltipTimer -= delta;
        if (this.bossTooltipTimer <= 0 && !this.bossTooltipHovered) {
          this.hideBossTooltip();
        }
      }
      this.drawBossHPBar(this.gameScene.activeBoss);
    } else {
      this.bossTooltipTimer = 0;
      this.bossTooltipHovered = false;
      this.hideBossHPBar();
    }
  }

  private bossHPBarBg!: Phaser.GameObjects.Graphics;
  private bossHPBarMsg!: Phaser.GameObjects.Text; // "BOSS" text or name
  
  // Boss Info UI
  private bossInfoIcon!: Phaser.GameObjects.Graphics;
  private bossInfoText!: Phaser.GameObjects.Text;
  private bossInfoHitArea!: Phaser.GameObjects.Rectangle;
  private bossTooltipBg!: Phaser.GameObjects.Graphics;
  private bossTooltipText!: Phaser.GameObjects.Text;
  private bossTooltipTimer: number = 0;
  private bossTooltipHovered: boolean = false;

  private drawBossHPBar(boss: Enemy): void {
    if (!this.bossHPBarBg) {
      this.bossHPBarBg = this.add.graphics();
      this.bossHPBarMsg = this.add.text(GAME_WIDTH / 2, 45, 'BOSS', {
        fontSize: '14px',
        fontFamily: 'Arial Black, sans-serif',
        color: '#ff0000',
        stroke: '#000000',
        strokeThickness: 3
      }).setOrigin(0.5);

      // Create info icon components
      this.bossInfoIcon = this.add.graphics();
      this.bossInfoText = this.add.text(0, 0, 'i', {
        fontSize: '14px',
        fontFamily: 'Arial Black, sans-serif',
        fontStyle: 'italic',
        color: '#ffffff',
      }).setOrigin(0.5);

      this.bossInfoHitArea = this.add.rectangle(0, 0, 24, 24).setInteractive({ useHandCursor: true }).setAlpha(0.001);

      // Create tooltip components
      this.bossTooltipBg = this.add.graphics().setDepth(1000).setVisible(false);
      this.bossTooltipText = this.add.text(0, 0, '', {
        fontSize: '12px',
        fontFamily: 'Arial, sans-serif',
        color: '#ffffff',
        lineSpacing: 2,
        padding: { left: 8, right: 8, top: 6, bottom: 6 },
        wordWrap: { width: 200 }
      }).setDepth(1001).setVisible(false);

      // Interactions — always read activeBoss at call time to avoid stale closure
      this.bossInfoHitArea.on('pointerover', () => {
        this.bossTooltipHovered = true;
        const currentBoss = this.gameScene.activeBoss;
        if (currentBoss) this.updateBossTooltip(currentBoss);
      });
      this.bossInfoHitArea.on('pointerout', () => {
        this.bossTooltipHovered = false;
        if (this.bossTooltipTimer <= 0) {
          this.hideBossTooltip();
        }
      });
      this.bossInfoHitArea.on('pointerdown', () => {
        this.bossTooltipTimer = 5000; // Show for 5 seconds
        const currentBoss = this.gameScene.activeBoss;
        if (currentBoss) this.updateBossTooltip(currentBoss);
      });
    }

    this.bossHPBarBg.clear();
    this.bossHPBarMsg.setVisible(true);
    this.bossInfoIcon.setVisible(true);
    this.bossInfoText.setVisible(true);
    this.bossInfoHitArea.setActive(true);

    const barW = 300;
    const barH = 16;
    const x = (GAME_WIDTH - barW) / 2;
    const y = 52; // Below legacy bar (which ends at y=48)

    // Background
    this.bossHPBarBg.fillStyle(0x000000, 0.8);
    this.bossHPBarBg.fillRect(x - 2, y - 2, barW + 4, barH + 4);

    // Red Fill
    const hpPercent = Math.max(0, boss.hp / boss.maxHP);
    this.bossHPBarBg.fillStyle(0xff0000, 1);
    this.bossHPBarBg.fillRect(x, y, barW * hpPercent, barH);

    // Border
    this.bossHPBarBg.lineStyle(2, 0xffffff, 0.5);
    this.bossHPBarBg.strokeRect(x - 2, y - 2, barW + 4, barH + 4);

    // Info Icon Positioning
    const iconX = x + barW + 16;
    const iconY = y + barH / 2;

    this.bossInfoIcon.clear();
    this.bossInfoIcon.fillStyle(0x4466cc, 1);
    this.bossInfoIcon.fillCircle(iconX, iconY, 10);
    this.bossInfoIcon.lineStyle(1.5, 0xffffff, 0.8);
    this.bossInfoIcon.strokeCircle(iconX, iconY, 10);

    this.bossInfoText.setPosition(iconX, iconY);
    this.bossInfoHitArea.setPosition(iconX, iconY);

    // Show boss name
    const bossName = boss.bossAbility
      ? (BOSS_DEFINITIONS.find(d => d.ability === boss.bossAbility)?.name ?? 'BOSS')
      : 'BOSS';
    this.bossHPBarMsg.setY(y + barH / 2);
    this.bossHPBarMsg.setText(`${bossName}: ${formatNumber(boss.hp)}/${formatNumber(boss.maxHP)}`);
    
    // Auto-update tooltip if it's open (e.g., if boss dies or ability changes)
    if (this.bossTooltipHovered || this.bossTooltipTimer > 0) {
      this.updateBossTooltip(boss);
    }
  }

  private updateBossTooltip(boss: Enemy): void {
    if (!boss.abilityDescription || boss.isDead) {
      this.hideBossTooltip();
      return;
    }

    this.bossTooltipText.setText(`Ability: ${boss.abilityDescription}`);
    
    const iconX = this.bossInfoHitArea.x;
    const iconY = this.bossInfoHitArea.y;
    const tw = this.bossTooltipText.width;
    const th = this.bossTooltipText.height;
    
    // Position below the icon
    let tx = iconX - tw / 2;
    let ty = iconY + 16;
    
    // Clamp to screen
    if (tx + tw > GAME_WIDTH - 4) tx = GAME_WIDTH - 4 - tw;
    
    this.bossTooltipBg.clear();
    this.bossTooltipBg.fillStyle(0x1a1a2e, 0.95);
    this.bossTooltipBg.fillRoundedRect(tx, ty, tw, th, 4);
    this.bossTooltipBg.lineStyle(1.5, 0x4466cc, 1);
    this.bossTooltipBg.strokeRoundedRect(tx, ty, tw, th, 4);

    this.bossTooltipText.setPosition(tx, ty);
    
    this.bossTooltipBg.setVisible(true);
    this.bossTooltipText.setVisible(true);
  }

  private hideBossTooltip(): void {
    if (this.bossTooltipBg) this.bossTooltipBg.setVisible(false);
    if (this.bossTooltipText) this.bossTooltipText.setVisible(false);
  }

  private hideBossHPBar(): void {
    if (this.bossHPBarBg) {
      this.bossHPBarBg.clear();
      this.bossHPBarMsg.setVisible(false);
      this.bossInfoIcon.setVisible(false);
      this.bossInfoText.setVisible(false);
      this.bossInfoHitArea.setActive(false);
      this.hideBossTooltip();
    }
  }
}
